import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  customWidthRangeCm,
  effectiveDpi,
  formatCm,
  POSTER_DEFAULT_DIN,
  POSTER_DIN_IDS,
  POSTER_LARGE_SHEET_COUNT,
  POSTER_LOW_DPI,
  type PosterDinId,
  type PosterPlan,
  type PosterSizeChoice,
  parseWidthCm,
  planPoster,
  type ResolvedPosterSize,
  resolvePosterSize,
} from "../../canvas-kit/export/poster-grid.js";
import { PosterInputError, type PosterPdfSource } from "../../canvas-kit/export/poster-pdf.js";
import type { ExportTarget } from "../export.js";
import { exportMediaPoster } from "./export-poster.js";
import { openPdfForPreview, renderPdfPagePreview } from "./pdf-preview.js";
import {
  type ImagePosterInfo,
  inspectImageForPoster,
  inspectPdfForPoster,
  prepareImagePosterSource,
} from "./source.js";

export interface PosterDialogProps {
  target: ExportTarget;
  kind: "image" | "pdf";
  /** 1-based page the lightbox showed; only used for PDFs. */
  initialPage: number;
  /** Filename without extension; the size suffix and `.pdf` get appended. */
  defaultFilename: string;
  onExported: (bytes: Uint8Array, filename: string) => Promise<void>;
  onClose: () => void;
}

type Loaded =
  | { kind: "image"; bytes: Uint8Array; info: ImagePosterInfo }
  | {
      kind: "pdf";
      bytes: Uint8Array;
      pages: Array<{ w: number; h: number }>;
      doc: PDFDocumentProxy;
    };

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; loaded: Loaded };

type SizeMode = { kind: "din"; paperId: PosterDinId } | { kind: "custom" };

const PREVIEW_MAX_PX = 800;
const LOAD_FAILED_MESSAGE = "Datei konnte nicht geladen werden.";
const EXPORT_FAILED_MESSAGE = "Poster konnte nicht erstellt werden.";

/** German message for the user; unexpected errors also go to the console. */
function messageFor(err: unknown, fallback: string): string {
  if (err instanceof PosterInputError) return err.message;
  console.error(err);
  return fallback;
}

const labelClass = "text-[10px] uppercase tracking-widest text-muted-foreground";
const infoBoxClass =
  "rounded-2xl border border-white/10 bg-white/2 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground";
const warnBoxClass =
  "rounded-2xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[11px] leading-snug text-amber-200";
const inputClass =
  "h-10 rounded-2xl border border-white/15 bg-white/4 px-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:bg-white/7";
const chipClass = (active: boolean) =>
  "flex-1 rounded-2xl border px-3 py-2.5 text-body-sm transition-colors disabled:opacity-50 " +
  (active
    ? "border-pitch-lime/60 bg-pitch-lime/20 text-pitch-lime"
    : "border-white/15 bg-white/4 hover:bg-white/10");
const stepperButtonClass =
  "flex h-10 w-10 items-center justify-center rounded-2xl border border-white/15 bg-white/4 transition-colors hover:bg-white/10 disabled:opacity-40";
const linkClass =
  "self-start text-[11px] text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground disabled:opacity-50";

export function PosterDialog({
  target,
  kind,
  initialPage,
  defaultFilename,
  onExported,
  onClose,
}: PosterDialogProps) {
  const title = "Als Poster drucken";
  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [step, setStep] = useState<"page" | "size">("size");
  const [page, setPage] = useState(1);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [sizeMode, setSizeMode] = useState<SizeMode>({ kind: "din", paperId: POSTER_DEFAULT_DIN });
  const [widthText, setWidthText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the file once. Whatever arrives after unmount is released at once.
  useEffect(() => {
    let disposed = false;
    let loaded: Loaded | null = null;
    const release = (l: Loaded) => {
      if (l.kind === "image") URL.revokeObjectURL(l.info.previewUrl);
      else void l.doc.destroy();
    };
    void (async () => {
      try {
        const bytes = await target.getBytes();
        if (disposed) return;
        let next: Loaded;
        if (kind === "image") {
          next = { kind: "image", bytes, info: await inspectImageForPoster(bytes, target.mime) };
        } else {
          const { pages } = await inspectPdfForPoster(bytes);
          next = { kind: "pdf", bytes, pages, doc: await openPdfForPreview(bytes) };
        }
        if (disposed) {
          release(next);
          return;
        }
        loaded = next;
        if (next.kind === "pdf") {
          setPage(Math.min(Math.max(1, Math.round(initialPage) || 1), next.pages.length));
          setStep(next.pages.length > 1 ? "page" : "size");
        }
        setLoad({ status: "ready", loaded: next });
      } catch (err) {
        if (!disposed) setLoad({ status: "error", message: messageFor(err, LOAD_FAILED_MESSAGE) });
      }
    })();
    return () => {
      disposed = true;
      if (loaded) release(loaded);
    };
  }, [target, kind, initialPage]);

  const loaded = load.status === "ready" ? load.loaded : null;
  const pdfDoc = loaded?.kind === "pdf" ? loaded.doc : null;

  // Re-render the page preview on page change; the old URL is revoked.
  useEffect(() => {
    if (!pdfDoc) return;
    let disposed = false;
    let url: string | null = null;
    setPdfPreviewUrl(null);
    setPreviewFailed(false);
    renderPdfPagePreview(pdfDoc, page, PREVIEW_MAX_PX)
      .then((u) => {
        if (disposed) {
          URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setPdfPreviewUrl(u);
      })
      .catch((err: unknown) => {
        if (disposed) return;
        console.error(err);
        setPreviewFailed(true);
      });
    return () => {
      disposed = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [pdfDoc, page]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // The lightbox underneath listens on window: keep Escape and the
      // arrow keys from closing or paging it while this dialog is open.
      if (e.key === "Escape" || e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.stopPropagation();
      }
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  // Aspect of what ends up in the PDF: post-EXIF output pixels, or the page
  // as a viewer shows it (CropBox + /Rotate).
  const aspect = useMemo(() => {
    if (!loaded) return null;
    if (loaded.kind === "image") return loaded.info.strategy.outW / loaded.info.strategy.outH;
    const p = loaded.pages[page - 1];
    return p ? p.w / p.h : null;
  }, [loaded, page]);

  const range = aspect === null ? null : customWidthRangeCm(aspect);
  const customActive = sizeMode.kind === "custom" && range !== null;
  // "Eigene" without a valid range for this page falls back to the default DIN
  // size; the chips show that fallback.
  const activeDin: PosterDinId | null = customActive
    ? null
    : sizeMode.kind === "din"
      ? sizeMode.paperId
      : POSTER_DEFAULT_DIN;

  const size: ResolvedPosterSize | null = useMemo(() => {
    if (aspect === null) return null;
    let choice: PosterSizeChoice;
    if (customActive && range) {
      const widthCm = parseWidthCm(widthText, range);
      if (widthCm === null) return null;
      choice = { kind: "custom", widthCm };
    } else {
      choice = { kind: "din", paperId: activeDin ?? POSTER_DEFAULT_DIN };
    }
    return resolvePosterSize(choice, aspect);
  }, [aspect, customActive, range, widthText, activeDin]);

  const plan: PosterPlan | null = useMemo(
    () => (size ? planPoster({ posterWMm: size.wMm, posterHMm: size.hMm }) : null),
    [size],
  );

  const selectCustom = () => {
    if (aspect === null || !range) return;
    if (sizeMode.kind !== "custom") {
      const a2Cm = Math.round(
        resolvePosterSize({ kind: "din", paperId: POSTER_DEFAULT_DIN }, aspect).wMm / 10,
      );
      setWidthText(String(Math.min(range.maxCm, Math.max(range.minCm, a2Cm))));
    }
    setSizeMode({ kind: "custom" });
  };

  const sheetCount = plan?.sheets.length ?? 0;
  const lowDpi =
    loaded?.kind === "image" &&
    size !== null &&
    effectiveDpi(loaded.info.strategy.outW, size.wMm) < POSTER_LOW_DPI;
  const largePoster = sheetCount >= POSTER_LARGE_SHEET_COUNT;
  const multiPagePdf = loaded?.kind === "pdf" && loaded.pages.length > 1;
  const previewUrl = loaded?.kind === "image" ? loaded.info.previewUrl : pdfPreviewUrl;

  const submit = async () => {
    if (!loaded || !size) return;
    setBusy(true);
    setError(null);
    try {
      const source: PosterPdfSource =
        loaded.kind === "image"
          ? await prepareImagePosterSource(loaded.bytes, target.mime, loaded.info.strategy)
          : { kind: "pdf", bytes: loaded.bytes, pageIndex: page - 1 };
      const pdf = await exportMediaPoster({ source, size });
      await onExported(pdf, `${defaultFilename}-${size.fileSuffix}.pdf`);
    } catch (err) {
      setError(messageFor(err, EXPORT_FAILED_MESSAGE));
      setBusy(false);
    }
  };

  const pageCount = loaded?.kind === "pdf" ? loaded.pages.length : 1;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-250 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="glass-floating w-[min(480px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-3xl p-4 shadow-2xl">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-body-sm font-semibold uppercase tracking-wider">{title}</h2>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            aria-label="Schließen"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white/6 hover:bg-white/10"
          >
            <X className="size-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (busy) return;
            if (step === "page") setStep("size");
            else void submit();
          }}
          className="flex flex-col gap-3"
        >
          {load.status === "loading" && (
            <div className={`${infoBoxClass} flex items-center gap-2`}>
              <Loader2 className="size-3.5 animate-spin" />
              Lade Datei…
            </div>
          )}

          {loaded && step === "page" && (
            <div className="flex flex-col gap-1">
              <span className={labelClass}>Seite wählen</span>
              <div className={`${infoBoxClass} flex h-[236px] items-center justify-center`}>
                {pdfPreviewUrl ? (
                  <img
                    src={pdfPreviewUrl}
                    alt={`Seite ${page}`}
                    className="max-h-[220px] max-w-full bg-white object-contain"
                  />
                ) : previewFailed ? (
                  <span>Vorschau nicht verfügbar</span>
                ) : (
                  <Loader2 className="size-4 animate-spin" />
                )}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Vorherige Seite"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={stepperButtonClass}
                >
                  <ChevronLeft className="size-4" />
                </button>
                <span className="flex-1 text-center text-body-sm tabular-nums">
                  Seite {page} von {pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Nächste Seite"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  className={stepperButtonClass}
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </div>
          )}

          {loaded && step === "size" && (
            <>
              <div className="flex flex-col gap-1">
                <span className={labelClass}>Postergröße</span>
                <div className="flex gap-1.5">
                  {POSTER_DIN_IDS.map((id) => (
                    <button
                      key={id}
                      type="button"
                      disabled={busy}
                      onClick={() => setSizeMode({ kind: "din", paperId: id })}
                      className={chipClass(activeDin === id)}
                    >
                      {id}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={busy || range === null}
                    onClick={selectCustom}
                    className={chipClass(customActive)}
                  >
                    Eigene
                  </button>
                </div>
              </div>

              {customActive && range && (
                <label className="flex flex-col gap-1">
                  <span className={labelClass}>Breite (waagerecht)</span>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={widthText}
                      onChange={(e) => setWidthText(e.target.value)}
                      disabled={busy}
                      className={`${inputClass} w-full pr-10`}
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-body-sm text-muted-foreground">
                      cm
                    </span>
                  </div>
                  <span className="text-[10px] leading-snug text-muted-foreground">
                    {range.minCm}–{range.maxCm} cm · Höhe ergibt sich aus der Datei
                  </span>
                </label>
              )}

              {multiPagePdf && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setStep("page")}
                  className={linkClass}
                >
                  Seite {page} · ändern
                </button>
              )}

              {size && plan && <PosterPreview plan={plan} previewUrl={previewUrl} />}

              <div className={infoBoxClass}>
                {size ? (
                  <>
                    {sheetCount} Blatt A4 + Anleitung · fertig {formatCm(size.wMm)} ×{" "}
                    {formatCm(size.hMm)} cm
                  </>
                ) : range ? (
                  <>
                    Breite zwischen {range.minCm} und {range.maxCm} cm eingeben
                  </>
                ) : null}
              </div>

              {lowDpi && <div className={warnBoxClass}>Bild wird bei dieser Größe unscharf</div>}
              {largePoster && (
                <div className={warnBoxClass}>Sehr großes Poster – {sheetCount} Blätter</div>
              )}
            </>
          )}

          {(load.status === "error" || error) && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-body-sm text-red-200">
              {load.status === "error" ? load.message : error}
            </div>
          )}

          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => !busy && onClose()}
              disabled={busy}
              className="h-10 rounded-full border border-white/15 bg-white/4 px-4 text-body-sm hover:bg-white/10 disabled:opacity-50"
            >
              Abbrechen
            </button>
            {load.status !== "error" && (
              <button
                type="submit"
                disabled={busy || !loaded || (step === "size" && !size)}
                className="h-10 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] shadow-glow hover:brightness-110 disabled:opacity-50"
              >
                {step === "page" ? "Weiter" : busy ? "Erstelle…" : "Poster erstellen"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The file at its print size over the A4 sheet grid, in poster millimetres
 * (poster top-left = 0,0; the grid is centred around it).
 */
function PosterPreview({ plan, previewUrl }: { plan: PosterPlan; previewUrl: string | null }) {
  const { grid, posterWMm, posterHMm } = plan;
  return (
    <div className="flex justify-center rounded-2xl border border-white/10 bg-white/2 px-3 py-2">
      <svg
        viewBox={`${-grid.offsetXMm} ${-grid.offsetYMm} ${grid.gridWMm} ${grid.gridHMm}`}
        className="h-auto max-h-[220px] w-full"
        role="img"
        aria-label={`${grid.cols} × ${grid.rows} Blätter`}
      >
        {previewUrl && (
          <image
            href={previewUrl}
            x={0}
            y={0}
            width={posterWMm}
            height={posterHMm}
            preserveAspectRatio="none"
          />
        )}
        {plan.sheets.map((sheet) => (
          <rect
            key={sheet.index}
            x={sheet.originXMm}
            y={sheet.originYMm}
            width={grid.sheetWMm}
            height={grid.sheetHMm}
            fill="rgba(255,255,255,0.04)"
            stroke="rgba(255,255,255,0.55)"
            strokeWidth={1}
            strokeDasharray="3 2"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <rect
          x={0}
          y={0}
          width={posterWMm}
          height={posterHMm}
          fill="none"
          stroke="rgba(204, 255, 102, 0.85)"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}
